import clsx from "clsx";
import { format } from "date-fns";

import Tooltip from "~/components/Tooltip.tsx";
import type { CalendarDay } from "~/modules/dashboard/components/TimeTrackingCalendar/calendarDays.ts";

type Props = {
	day: CalendarDay;
	isBooked: boolean;
	openDay: (date: Date) => void;
};

/**
 * The header of a day of the shown month opens the day view. A day of a neighbor month is no button, as in the compact grid.
 * A booked day gets a green circle by the `DayState` of the backend (see `isDayBooked`), not by 8 hours. Today keeps its
 * orange circle and shows a booked state as a green border.
 */
export function DayHeader({ day, isBooked, openDay }: Props) {
	const content = (
		<>
			<span className={clsx("flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
				day.isToday ? "bg-secondary-500 text-white" : isBooked ? "bg-success-500 text-white" : day.isInMonth ? "text-gray-700" : "text-gray-300",
				day.isToday && isBooked && "border-2 border-success-500")}>
				{format(day.date, "d")}
			</span>
			{day.holiday && (
				<Tooltip text={day.holiday.displayName}>
					<span className={clsx("min-w-0 truncate text-xs", day.isWeekend ? "text-gray-500" : "text-gray-400", !day.isInMonth && "opacity-50")}>{day.holiday.displayName}</span>
				</Tooltip>
			)}
		</>
	);
	const className = "flex min-w-0 items-center gap-1";

	if (!day.isInMonth) return <div className={className}>{content}</div>;

	return (
		<button type="button"
				aria-label={`Tag ${format(day.date, "dd.MM.yyyy")} öffnen`}
				onClick={() => openDay(day.date)}
				className={clsx(className, "w-full rounded text-left hover:bg-gray-100")}>
			{content}
		</button>
	);
}
